import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getEventRoutePaths } from '../router/routeRegistry';
import { getSiteConfig } from '../libs/common/siteConfig';
import { getCurrentTheme } from '../libs/common/themeConfig';
import { getLibraryGroups, getEventFlag, getEventLogo } from '../libs/common/eventLibrary';
import { useTranslation } from '../libs/common/i18n';
import './eventLibrary.css';

const LibraryContext = createContext(null);
const compactViewport = () => typeof window !== 'undefined' && (!window.matchMedia || window.matchMedia('(max-width: 1099px)').matches);
export const useEventLibrary = () => useContext(LibraryContext);

export function EventLibraryContent({ children }) {
  const content = useRef(null);
  const { pathname } = useLocation();
  useEffect(() => { content.current?.scrollTo({ top: 0, left: 0 }); }, [pathname]);
  return <div className="event-library-content" ref={content}>{children}</div>;
}

export function EventLibraryProvider({ children }) {
  const [compact, setCompact] = useState(compactViewport);
  const [open, setOpen] = useState(() => !compactViewport());
  const trigger = useRef(null);
  const location = useLocation();
  useEffect(() => {
    const media = window.matchMedia?.('(max-width: 1099px)');
    if (!media) return undefined;
    const resize = () => { setCompact(media.matches); setOpen(!media.matches); };
    media.addEventListener('change', resize);
    return () => media.removeEventListener('change', resize);
  }, []);
  useEffect(() => { if (compact) setOpen(false); }, [location.pathname, compact]);
  return <LibraryContext.Provider value={{ compact, open, setOpen, trigger }}>{children}</LibraryContext.Provider>;
}

export function EventLibrary() {
  const { compact, open, setOpen, trigger } = useEventLibrary();
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState({});
  const panel = useRef(null);
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const site = getSiteConfig().type;
  const theme = getCurrentTheme();
  const groups = getLibraryGroups(getEventRoutePaths(site), site, query);
  const eventCount = groups.reduce((count, group) => count + 1 + group.children.length, 0);
  const years = [...new Set(groups.map(group => group.event.year))];
  const close = () => { setOpen(false); trigger.current?.focus(); };

  useEffect(() => {
    if (!open || !compact) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panel.current?.querySelector('input')?.focus();
    return () => { document.body.style.overflow = previousOverflow; };
  }, [open, compact]);

  if (!open) return compact ? null : <aside id="event-library" className="event-library-rail" aria-label={t('eventLibrary')}>
    <button type="button" aria-label={t('eventLibrary')} title={t('eventLibrary')} aria-expanded={false} onClick={() => setOpen(true)}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true"><path strokeWidth="2" d="M3 5h18M3 12h18M3 19h18M8 5v14" /></svg>
    </button>
  </aside>;
  const handleKey = event => {
    if (event.key === 'Escape') { event.preventDefault(); close(); }
    if (!compact || event.key !== 'Tab') return;
    const controls = [...panel.current.querySelectorAll('a, button, input')];
    const first = controls[0];
    const last = controls[controls.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  };
  return <>
    {compact && <button className="event-library-scrim" aria-label={t('closeEventLibrary')} onClick={close} tabIndex={-1} />}
    <aside id="event-library" className={`event-library ${compact ? 'event-library-drawer' : ''}`} ref={panel}
      role={compact ? 'dialog' : undefined} aria-modal={compact ? true : undefined} aria-label={t('eventLibrary')} onKeyDown={handleKey}>
      <div className="event-library-heading"><h2>{t('eventLibrary')}</h2><button type="button" aria-label={t('closeEventLibrary')} onClick={close}>×</button></div>
      <label className="event-library-search"><span className="sr-only">{t('searchEvents')}</span>
        <input type="search" placeholder={t('searchEvents')} value={query} onChange={event => setQuery(event.target.value)} />
      </label>
      <div className="event-library-scroll">
        {!groups.length && <p role="status" className="event-library-empty">{t('noEventsFound')}</p>}
        <nav aria-label={t('eventLibrary')}>
          {years.map(year => <section key={year} className="event-library-year" aria-labelledby={`library-year-${year}`}>
            <h3 id={`library-year-${year}`}>{year || t('otherEvents')}</h3>
            <ul>{groups.filter(group => group.event.year === year).map(({ event, children }) => {
              const isExpanded = Boolean(query.trim()) || (expanded[event.path] ?? children.some(child => child.path === pathname));
              const submenuId = `library-submenu-${event.category}`;
              return <li key={event.path}>
              <div className="event-library-event-row">
              <Link to={event.path} aria-current={pathname === event.path ? 'page' : undefined}>
                <img className="event-library-flag" src={getEventFlag(event.location).src} alt={getEventFlag(event.location).label} width="18" height="18" loading="lazy" />
                <img className="event-library-logo" src={getEventLogo(event, site, theme)} alt="" width="18" height="18" loading="lazy" />
                <span className="event-library-info">
                  <span className="event-library-name">{event.name}</span>
                  <span className="event-library-meta">{event.type}{event.startDate ? ` · ${event.startDate.slice(5)}` : ''}</span>
                </span>
              </Link>
              {children.length > 0 && <button className="event-library-submenu-toggle" type="button" aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${event.name} subevents`} aria-expanded={isExpanded} aria-controls={submenuId} onClick={() => setExpanded(previous => ({ ...previous, [event.path]: !isExpanded }))}>
                <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" aria-hidden="true" style={{ transform: isExpanded ? 'rotate(90deg)' : undefined }}><path strokeWidth="2" d="m6 3 5 5-5 5" /></svg>
              </button>}
              </div>
              {children.length > 0 && isExpanded && <ul id={submenuId} className="event-library-submenu" aria-label={`${event.name} subevents`}>
                {children.map(child => <li key={child.path}><Link to={child.path} aria-current={pathname === child.path ? 'page' : undefined}>
                  <img className="event-library-flag" src={getEventFlag(child.location).src} alt={getEventFlag(child.location).label} width="18" height="18" loading="lazy" />
                  <span className="event-library-info"><span className="event-library-name">{child.name.split(' - ').pop()}</span><span className="event-library-meta">{child.startDate?.slice(5)}{child.location && ` · ${child.location}`}</span></span>
                </Link></li>)}
              </ul>}
            </li>;
            })}</ul>
          </section>)}
        </nav>
      </div>
      <div className="event-library-count">{eventCount} {t('eventsLabel')}</div>
    </aside>
  </>;
}
